// The page-action steps that change records — `txn_action` and `txn_update` —
// and `refresh_source`. They never build a URL: each surface supplies the
// engine transport it already uses for transaction screens (`ActionCtx.execute`
// — the platform's live-preview/site-txn executor, the downloaded runtime's
// /crud/execute), so rights, the button's own rules, approvals, the audit
// trail and IOFlow events apply exactly as if the button were pressed on the
// transaction screen. The runner stays transport-free.

import type { TxnActionStep, TxnUpdateStep } from '@twasta/page-schema'
import { substituteVars } from './substitute'
import { parseIdList } from '../live/ids'
import { attemptActionWithConfirm } from '../txn/ActionConfirmModal'
import type { RunActionResponse } from '../txn/contracts'
import type { ActionCtx } from './runner'

export interface TxnStepResult {
  ok: boolean
  response?: unknown
  status?: number
  errorMessage?: string
  /** A success line worth showing when the author gave no on_success branch. */
  notice?: string
}

/** A human message out of any surface's error shape: the platform's axios
 *  envelope (`response.data.error`), FastAPI `detail`, or the downloaded
 *  runtime's fetch error (`detail.summary` / `detail.errors`). */
export function stepErrorMessage(err: unknown): { message: string; status?: number } {
  const e = err as {
    response?: { status?: number; data?: { error?: { message?: string; code?: string; details?: { errors?: Array<{ message?: string }> } }; detail?: unknown } }
    detail?: { summary?: string; message?: string; errors?: Array<{ message?: string }> } | string
    status?: number
    message?: string
  }
  const status = e?.response?.status ?? e?.status
  const env = e?.response?.data
  if (env?.error) {
    const first = env.error.details?.errors?.find((x) => x?.message)?.message
    return { message: first || env.error.message || 'The request was refused', status }
  }
  const detail = env?.detail ?? e?.detail
  if (typeof detail === 'string' && detail.trim()) return { message: detail, status }
  if (detail && typeof detail === 'object') {
    const d = detail as { summary?: string; message?: string; errors?: Array<{ message?: string }> }
    const first = d.errors?.find((x) => x?.message)?.message
    if (first || d.summary || d.message) return { message: (first || d.summary || d.message) as string, status }
  }
  return { message: e?.message || 'The request failed', status }
}

async function attempt(
  ctx: ActionCtx,
  call: (confirm?: unknown) => Promise<unknown>,
): Promise<RunActionResponse> {
  const first = (confirm?: unknown) => call(confirm) as Promise<RunActionResponse>
  if (ctx.confirmAction) return attemptActionWithConfirm(first, ctx.confirmAction)
  const res = await first()
  if (res?.handler === 'confirm') {
    throw new Error('This action asks for a confirmation this screen cannot show.')
  }
  return res
}

export async function runTxnActionStep(step: TxnActionStep, ctx: ActionCtx): Promise<TxnStepResult | null> {
  if (!ctx.execute) return { ok: false, errorMessage: 'Running a transaction action is not available on this surface' }
  const obj = substituteVars(step.obj_name, ctx.resolve).trim()
  const actionName = substituteVars(step.action, ctx.resolve).trim()
  if (!obj || !actionName) return { ok: false, errorMessage: 'txn_action: object and action are required' }
  const ids = parseIdList(substituteVars(step.ids, ctx.resolve))
  // Nothing selected / an unresolved token: nothing to act on. Not an error —
  // a "Done" button pressed with an empty selection simply does nothing.
  if (!ids.length || ids.some((k) => k.includes('{{'))) return null
  // No form_no = the header form (the engine's own default).
  const form = step.form_no ? { form_no: String(step.form_no) } : null
  try {
    const res = ids.length === 1
      ? await attempt(ctx, (confirm) => ctx.execute!(obj, 'run_action', {
          ...form, action_name: actionName, header: {}, header_values: {}, txn_id: ids[0],
          ...(confirm ? { confirm } : null),
        }))
      : await attempt(ctx, (confirm) => ctx.execute!(obj, 'run_action_bulk', {
          ...form, action_name: actionName, txn_ids: ids,
          ...(confirm ? { confirm } : null),
        }))
    if (res?.handler === 'bulk_result') {
      const s = res.summary
      const bad = (res.results || []).filter((r) => r.status !== 'success')
      if (bad.length) {
        const total = s?.total ?? ids.length
        const why = bad.find((r) => r.message)?.message
        return { ok: false, response: res, errorMessage: `${bad.length} of ${total} not done${why ? `: ${why}` : ''}` }
      }
      return { ok: true, response: res }
    }
    return { ok: true, response: res, notice: res?.handler === 'message' ? (res as { message?: string }).message : undefined }
  } catch (err) {
    const m = stepErrorMessage(err)
    return { ok: false, errorMessage: m.message, status: m.status }
  }
}

export async function runTxnUpdateStep(step: TxnUpdateStep, ctx: ActionCtx): Promise<TxnStepResult | null> {
  if (!ctx.execute) return { ok: false, errorMessage: 'Updating a record is not available on this surface' }
  const obj = substituteVars(step.obj_name, ctx.resolve).trim()
  const id = substituteVars(step.id, ctx.resolve).trim()
  if (!obj) return { ok: false, errorMessage: 'txn_update: object is required' }
  if (!id || id.includes('{{')) return null
  const header: Record<string, string> = {}
  for (const [col, v] of Object.entries(step.values || {})) header[col] = substituteVars(v, ctx.resolve)
  if (!Object.keys(header).length) return { ok: false, errorMessage: 'txn_update: no values to set' }
  const payload: Record<string, unknown> = { txn_id: id, header, partial: true }
  if (step.form_no) payload.form_no = String(step.form_no)
  if (step.expect && Object.keys(step.expect).length) {
    const expect: Record<string, string> = {}
    for (const [col, v] of Object.entries(step.expect)) expect[col] = substituteVars(v, ctx.resolve)
    payload.expect = expect
  }
  try {
    const res = await ctx.execute(obj, 'edit', payload)
    return { ok: true, response: res }
  } catch (err) {
    const m = stepErrorMessage(err)
    return { ok: false, errorMessage: m.message, status: m.status }
  }
}

/** Re-read data sources: `undefined` = every live-bound source, `[]` = none. */
export async function refreshAfter(list: string[] | undefined, ctx: ActionCtx): Promise<void> {
  if (!ctx.refreshSources) return
  if (Array.isArray(list) && list.length === 0) return
  try {
    await ctx.refreshSources(Array.isArray(list) ? list : undefined)
  } catch { /* a failed re-read keeps what the page shows */ }
}
